import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { buyCredits, getMe, listCreditPacks, subscribe, type CreditPack } from '@/api';
import { isLoggedIn, getUser, getToken, setToken } from '@/auth/token';
import Header from './components/Header';
import Hero from './components/Hero';
import SiteGallery from './components/SiteGallery';
import HowItStaysGood from './components/HowItStaysGood';
import OutcomeShowcase from './components/OutcomeShowcase';
import StudioSection from './components/StudioSection';
import Pricing from './components/Pricing';
import FAQ from './components/FAQ';
import Footer from './components/Footer';

const FALLBACK_PACKS: CreditPack[] = [
  { id: 'starter', name: 'Starter', credits: 20, price_cents: 1000, blurb: 'About 20 hosted model runs' },
  { id: 'builder', name: 'Builder', credits: 60, price_cents: 2500, blurb: 'About 60 runs, or 15 improvement loops' },
  { id: 'lab', name: 'Lab', credits: 150, price_cents: 5000, blurb: 'Volume refills, no SLA' },
];

export default function Landing() {
  const [packs, setPacks] = useState<CreditPack[]>(FALLBACK_PACKS);
  const [busy, setBusy] = useState<string | null>(null);
  const [hostBusy, setHostBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const navigate = useNavigate();
  const logged = isLoggedIn();
  const [isAdmin, setIsAdmin] = useState(getUser()?.role === 'admin');

  useEffect(() => {
    document.title = 'Sourcetape — Launchpad for founders';
  }, []);

  useEffect(() => {
    if (!isLoggedIn()) return;
    getMe()
      .then((u) => {
        const tok = getToken();
        if (tok) setToken(tok, u);
        setIsAdmin(u.role === 'admin');
      })
      .catch(() => { /* ignore */ });
  }, []);

  useEffect(() => {
    listCreditPacks()
      .then((res) => { if (res.packs?.length) setPacks(res.packs); })
      .catch(() => { /* keep fallback */ });
  }, []);

  const host = async () => {
    setErr(null);
    if (!logged) {
      navigate(`/login?next=${encodeURIComponent('/dashboard?view=credits')}`);
      return;
    }
    setHostBusy(true);
    try {
      const res = await subscribe('hosting');
      if (res.url) {
        window.location.assign(res.url);
        return;
      }
      navigate('/dashboard?view=credits');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'checkout failed');
    } finally {
      setHostBusy(false);
    }
  };

  const buy = async (packId: string) => {
    setErr(null);
    if (!logged) {
      navigate(`/login?next=${encodeURIComponent(`/dashboard?view=credits&pack=${packId}`)}`);
      return;
    }
    setBusy(packId);
    try {
      const res = await buyCredits(packId);
      if (res.url) {
        window.location.assign(res.url);
        return;
      }
      navigate('/dashboard?view=credits');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'checkout failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-bg text-textp overflow-y-auto" data-testid="landing">
      <Header logged={logged} isAdmin={isAdmin} />
      <main id="top">
        <Hero logged={logged} />
        <SiteGallery />
        <HowItStaysGood />
        <OutcomeShowcase />
        <Pricing packs={packs} busy={busy} err={err} hostingBusy={hostBusy} onBuy={buy} onHost={host} />
        <FAQ />
        <StudioSection />
      </main>
      <Footer logged={logged} />
    </div>
  );
}
